import React from 'react';
import { useImpersonation } from '../contexts/ImpersonationContext';
import { Button } from './ui/button';
import { Eye, EyeOff, X, Shield, AlertTriangle } from 'lucide-react';

/**
 * ImpersonationBanner - Shows when a Super Admin/Founder is viewing the app as another user
 * 
 * Features:
 * - Prominent banner that can't be missed
 * - Shows who you're impersonating with role and client info
 * - Read-only mode toggle with confirmation
 * - Quick exit button
 */
export const ImpersonationBanner = () => {
  const { 
    isImpersonating, 
    targetUser, 
    readOnlyMode, 
    toggleReadOnlyMode, 
    stopImpersonation 
  } = useImpersonation();

  if (!isImpersonating || !targetUser) {
    return null;
  }

  const handleToggleReadOnly = () => {
    if (readOnlyMode) {
      // Confirm before enabling write mode
      if (window.confirm(
        'Are you sure you want to enable Full Access mode?\n\n' +
        'Any changes you make will be logged and attributed to your account, ' +
        'but will affect the data this user sees.\n\n' +
        'This should only be used for troubleshooting purposes.'
      )) {
        toggleReadOnlyMode();
      }
    } else {
      toggleReadOnlyMode();
    }
  };

  return (
    <div 
      className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-amber-950 shadow-lg"
      data-testid="impersonation-banner"
    >
      <div className="max-w-screen-2xl mx-auto px-4 py-2 flex items-center justify-between gap-4">
        {/* Left: Impersonation info */}
        <div className="flex items-center gap-3 min-w-0">
          <div className="flex items-center gap-2 flex-shrink-0">
            <Eye className="w-5 h-5" />
            <span className="font-semibold text-sm hidden sm:inline">Viewing as:</span>
          </div>
          
          <div className="flex items-center gap-2 min-w-0">
            <span className="font-bold truncate">
              {targetUser.full_name}
            </span>
            <span className="text-amber-800 text-sm hidden md:inline">
              ({targetUser.email})
            </span>
          </div>
          
          <span className="px-2 py-0.5 bg-amber-600 text-amber-100 text-xs font-medium rounded-full flex-shrink-0">
            {targetUser.role_display}
          </span>
          
          {targetUser.client_names?.length > 0 && (
            <span className="text-amber-800 text-sm hidden lg:inline truncate">
              • {targetUser.client_names.join(', ')}
            </span>
          )}
        </div>

        {/* Right: Mode toggle and exit */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {/* Read-only indicator/toggle */}
          <Button
            onClick={handleToggleReadOnly}
            variant="ghost"
            size="sm"
            className={`gap-1.5 text-xs font-medium ${
              readOnlyMode 
                ? 'bg-amber-600/50 text-amber-100 hover:bg-amber-600' 
                : 'bg-red-600 text-white hover:bg-red-700'
            }`}
            data-testid="impersonation-mode-toggle"
          >
            {readOnlyMode ? (
              <>
                <Shield className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Read-Only</span>
              </>
            ) : (
              <>
                <AlertTriangle className="w-3.5 h-3.5" />
                <span className="hidden sm:inline">Full Access</span>
              </>
            )}
          </Button>

          {/* Exit button */}
          <Button
            onClick={stopImpersonation}
            variant="ghost"
            size="sm"
            className="gap-1.5 text-xs font-medium bg-amber-700 text-amber-100 hover:bg-amber-800"
            data-testid="impersonation-exit"
          >
            <X className="w-3.5 h-3.5" />
            <span className="hidden sm:inline">Exit</span>
          </Button>
        </div>
      </div>
    </div>
  );
};

export default ImpersonationBanner;
